import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { getAlerts } from 'apis/AlertsAPI';
import ChevronRight from 'common/Icons/ChevronRight';
import { useSidebarMenuLogic } from 'components/layout/hooks';
import DetailsButton from 'components/lib/DetailsButton';
import ErrorMessage from 'components/lib/ErrorMessage';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { getFormattedDate, getRelativeTime } from 'helpers/dates';
import { useAppSelector } from 'store/hook';
import { AlertListItemType } from 'types/alerts';

import AlertActionPlanCard from './AlertActionPlanCard';
import AlertsHeader from './AlertsHeader';
import AlertsNotFound from './AlertsNotFound';
import AlertTypeTag from './AlertTypeTag';

const Container = styled.div`
  width: 100%;
`;

const Main = styled.div<{ isEmpty: boolean }>`
  padding: 20px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: ${({ isEmpty }) => (isEmpty ? 'var(--white)' : 'var(--background-gray-light)')};
  border-top: 1px solid var(--line-gray);
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 80px;
`;

const AlertCard = styled.div`
  display: flex;
  background: var(--white);
  max-width: 1175px;
  margin: auto;
`;

const AlertAccent = styled.div`
  width: 5px;
  border-radius: 4px;
  background: linear-gradient(to bottom, #f5d47d, #e8a947);
`;

const AlertContent = styled.div`
  width: 100%;
  padding: 20px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const DateMeta = styled.div`
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RelativeDateText = styled.span`
  color: var(--text-primary);
`;

const DateText = styled.span`
  color: var(--text-secondary);
`;

const AlertTitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  cursor: pointer;
`;

const AlertTitle = styled.div`
  font-size: 16px;
  font-family: var(--font-bold);
`;

const CommunityName = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-secondary);
`;

const InsightHeader = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-family: var(--font-bold);
`;

const InsightIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const InsightDescription = styled.div`
  margin-top: 5px;
  font-size: 14px;
  color: var(--text-secondary);
`;

const Footer = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const ChevronIconContainer = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Alerts = () => {
  const navigate = useNavigate();
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { showActionPlans } = useSidebarMenuLogic();

  const {
    data = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['alerts', selectedCommunity?.id],
    queryFn: () => getAlerts(selectedCommunity?.id ?? -1),
    enabled: Boolean(selectedCommunity?.id),
    refetchOnWindowFocus: false,
  });

  const handleOpenAlertDetails = (alertId: number) => {
    navigate(`${alertId}`);
  };

  const isEmptyState = !isLoading && !isError && data.length === 0;

  const handleOpenActionPlanDetails = (alert: AlertListItemType) => {
    if (!alert.action_plan_id || !showActionPlans) {
      return;
    }

    const communityId = selectedCommunity?.id ?? alert.community_id;
    navigate(`/community/${communityId}/action-plans/${alert.action_plan_id}`);
  };

  return (
    <Container data-test-id="alerts-page">
      <AlertsHeader />
      <Main data-test-id="alerts-main" isEmpty={isEmptyState}>
        <div data-test-id="alerts-error-message">
          <ErrorMessage text="Failed to load alerts. Please try again." active={isError} />
        </div>

        {isLoading && (
          <LoaderContainer>
            <LoaderRibbed active />
          </LoaderContainer>
        )}

        {isEmptyState && <AlertsNotFound />}

        {!isLoading &&
          !isError &&
          data.map((alert) => {
            return (
              <AlertCard key={alert.id} data-test-id={`alerts-card-${alert.id}`}>
                <AlertAccent />
                <AlertContent>
                  <TopRow>
                    <DateMeta>
                      <RelativeDateText>{getRelativeTime(alert.created_at)}</RelativeDateText>
                      <DateText>{getFormattedDate(alert.created_at, 'MMM D YYYY')}</DateText>
                    </DateMeta>
                    {alert.type_name && <AlertTypeTag typeName={alert.type_name} />}
                  </TopRow>

                  <AlertTitleRow onClick={() => handleOpenAlertDetails(alert.id)}>
                    <AlertTitle>{alert.title}</AlertTitle>
                    <ChevronIconContainer>
                      <ChevronRight fill="var(--text-primary)" width={20} height={20} />
                    </ChevronIconContainer>
                  </AlertTitleRow>

                  <CommunityName>
                    {alert.related_communities?.length
                      ? alert.related_communities.map((community) => community.comunity_name).join(', ')
                      : alert.community_name}
                  </CommunityName>

                  <InsightHeader>
                    <InsightIcon src="/icons/ai-blob.svg" alt="ai-insights" />
                    <span>AI Insights</span>
                  </InsightHeader>
                  <InsightDescription>{alert.description}</InsightDescription>

                  <Footer>
                    {alert.action_plan_id ? (
                      <AlertActionPlanCard
                        actionPlanName={alert.action_plan_name}
                        isArchived={Boolean(alert.action_plan_archived)}
                        disabled={!showActionPlans || Boolean(alert.action_plan_archived)}
                        onClick={() => handleOpenActionPlanDetails(alert)}
                      />
                    ) : (
                      <div />
                    )}
                    <DetailsButton onClick={() => handleOpenAlertDetails(alert.id)} />
                  </Footer>
                </AlertContent>
              </AlertCard>
            );
          })}
      </Main>
    </Container>
  );
};

export default Alerts;
